import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ResourceService } from './resource';
import { CacheService } from '../cache';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PermissionService extends ResourceService<any> {
    constructor(protected http: HttpClient, protected cache: CacheService) {
        super(http, cache, 'permissions');
    }

    /**
     * Sends a GET request to the /permissions/my-permissions endpoint
     * @remarks
     * to get my permissions
     * @returns {Observable<any>} Returns an observable of any
     */
    getMyPermissions(): Observable<any> {
        const headers = new HttpHeaders()
            .set('X-Requested-With', 'admin');
        return this.http.get<any>(`${this.endpoint}/my-permissions`, { headers });
    }

    /**
     * Sends a GET request to the /permissions/codes endpoint
     * @remarks
     * to get all permission codes
     * @returns {Observable<any>} Returns an observable of any
     */
    getPermissionCodes(): Observable<any> {
        const headers = new HttpHeaders()
            .set('X-Requested-With', 'admin');
        return this.http.get<any>(`${this.endpoint}/codes`, { headers });
    }

    /**
     * Sends a DELETE request to the /permissions/:id endpoint
     * @remarks
     * to delete a permission
     * @param {string} id Permission ID
     * @returns {Observable<any>} Returns an observable of any
     */
    deletePermission(id: string): Observable<any> {
        const headers = new HttpHeaders().set('X-Requested-With', 'admin');
        return this.http.delete<any>(`${this.endpoint}/${id}`, { headers });
    }

    /**
     * Sends a GET request to the /permissions/:id endpoint
     * @remarks
     * to get permission by id
     * @param {string} id Permission ID
     * @returns {Observable<any>} Returns an observable of any
     */
    getPermissionById(id: string): Observable<any> {
        const headers = new HttpHeaders().set('X-Requested-With', 'admin');
        return this.http.get<any>(`${this.endpoint}/${id}`, { headers });
    }

    /**
     * Sends a GET request to the /permissions endpoint
     * @remarks
     * to get permissions
     * @param {number} page Page number
     * @param {number} limit Number of items per page
     * @returns {Observable<any>} Returns an observable of any
     */
    getPermissions(page: number = 1, limit: number = 50): Observable<any> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString());
        const headers = new HttpHeaders().set('X-Requested-With', 'admin');
        return this.http.get<any>(`${this.endpoint}`, { params, headers });
    }

    /**
     * Sends a PUT request to the /permissions/:id endpoint
     * @remarks
     * to update a permission
     * @param {string} id Permission ID
     * @param {any} payload Request payload
     * @returns {Observable<any>} Returns an observable of any
     */
    updatePermission(id: string, payload: any): Observable<any> {
        const headers = new HttpHeaders().set('X-Requested-With', 'admin');
        return this.http.put<any>(`${this.endpoint}/${id}`, payload, { headers });
    }

    /**
     * Sends a POST request to the /permissions endpoint
     * @remarks
     * to create a new permission
     * @param {any} payload Request payload
     * @returns {Observable<any>} Returns an observable of any
     */
    addPermission(payload: any): Observable<any> {
        const headers = new HttpHeaders().set('X-Requested-With', 'admin');
        return this.http.post<any>(`${this.endpoint}`, payload, { headers });
    }
}
